/**
 * API client for status mapping endpoints
 */

import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface StatusMappingSuggestion {
    id: number;
    gh_id: string;
    emr_source: string;
    emr_status_id: number;
    emr_status_name: string;
    suggested_ghl_status_id: number | null;
    suggested_ghl_status_code: string | null;
    suggested_ghl_status_label: string | null;
    confidence_score: number;
    reasoning: string;
    risk_factors: string[] | null;
    alternative_matches: Array<{
        ghl_status_id: number;
        confidence: number;
        reason: string;
    }> | null;
    status: 'pending' | 'approved' | 'rejected' | 'engine_mapped';
    is_reviewed: boolean;
    reviewed_by: number | null;
    reviewed_by_name: string | null;
    reviewed_at: string | null;
    created_at: string;
    is_external?: boolean;
    /** Engine coverage: both | emr_to_ghl | ghl_to_emr */
    mapping_coverage?: 'both' | 'emr_to_ghl' | 'ghl_to_emr' | string | null;
    /** Approved rows: the Advital status this EMR status really syncs to in the engine */
    engine_ghl_status?: string | null;
    /** Approved rows: the EMR status the Advital status syncs back to, when it is a different one */
    ghl_to_emr_emr_status_name?: string | null;
}

export interface TriggerMappingRequest {
    gh_id: string;
    emr_status_id?: number;
}

export interface TriggerMappingResponse {
    status: string;
    message: string;
    suggestions_created: number;
}

export interface ReviewRequest {
    action: 'approve' | 'reject';
    notes?: string;
    use_for_ghl_to_emr?: boolean;
}

export interface StatsResponse {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
}

export const statusMappingApi = {
    /**
     * Trigger status mapping for a GHL location
     */
    async triggerMapping(request: TriggerMappingRequest): Promise<TriggerMappingResponse> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/trigger/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to trigger mapping'));
        }

        return response.json();
    },

    /**
     * Get status mapping suggestions
     */
    async getSuggestions(
        ghId: string,
        filters?: {
            status?: string;
            minConfidence?: number;
            maxConfidence?: number;
            limit?: number;
            offset?: number;
            emrStatusName?: string;
            ghlStatusLabel?: string;
            search?: string;
        }
    ): Promise<{ total: number; suggestions: StatusMappingSuggestion[] }> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.minConfidence !== undefined) params.append('min_confidence', filters.minConfidence.toString());
        if (filters?.maxConfidence !== undefined) params.append('max_confidence', filters.maxConfidence.toString());
        if (filters?.limit) params.append('limit', filters.limit.toString());
        if (filters?.offset) params.append('offset', filters.offset.toString());
        if (filters?.emrStatusName) params.append('emr_status_name', filters.emrStatusName);
        if (filters?.ghlStatusLabel) params.append('ghl_status_label', filters.ghlStatusLabel);
        if (filters?.search) params.append('search', filters.search);

        const response = await authFetch(
            `${API_BASE_URL}/status-mapping/suggestions/${ghId}?${params.toString()}`
        );

        if (!response.ok) {
            throw new Error('Failed to fetch suggestions');
        }

        return response.json();
    },

    /**
     * Get distinct values per filterable column, for populating dropdown filters.
     */
    async getFilterOptions(ghId: string): Promise<{ emr_status_name: string[]; ghl_status_label: string[] }> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/filter-options/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch filter options');
        return response.json();
    },

    /**
     * Review a suggestion (approve or reject)
     */
    async reviewSuggestion(suggestionId: number, request: ReviewRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/review/${suggestionId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to review suggestion'));
        }
    },

    /**
     * Permanently delete a mapping suggestion
     */
    async deleteSuggestion(suggestionId: number): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/${suggestionId}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to delete suggestion'));
        }
    },

    /**
     * Make an approved mapping's EMR status the one its Advital status syncs back to
     */
    async useForGhlToEmr(suggestionId: number): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/${suggestionId}/use-for-ghl-to-emr`, {
            method: 'POST',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to update Advital → EMR mapping'));
        }
    },

    /**
     * Get statistics for status mappings
     */
    async getStats(ghId: string): Promise<StatsResponse> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/stats/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch stats');
        }

        return response.json();
    },
};

// Manual mapping interfaces and methods
export interface GHLStatus {
    id: number;
    status_code: string;
    status_label: string;
    description?: string;
}

export interface EMRStatus {
    id: number;
    name: string;
}

export interface ManualMappingRequest {
    gh_id: string;
    emr_status_id: number;
    emr_status_name: string;
    ghl_status_id: number;
    ghl_status_code: string;
}

export const statusMappingManualApi = {
    /**
     * Get all GHL statuses for manual mapping
     */
    async getGHLStatuses(ghId: string): Promise<GHLStatus[]> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/ghl-statuses/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch Advital statuses');
        }

        const data = await response.json();
        return data.statuses;
    },

    /**
     * Get all EMR statuses for manual mapping
     */
    async getEMRStatuses(ghId: string): Promise<EMRStatus[]> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/emr-statuses/${ghId}`);

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to fetch EMR statuses');
        }

        const data = await response.json();
        return data.statuses;
    },

    /**
     * Create a manual mapping
     */
    async createManualMapping(request: ManualMappingRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/manual`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to create manual mapping'));
        }
    },

    /**
     * Update an existing mapping row (Edit button on mappings table)
     */
    async updateManualMapping(
        suggestionId: number,
        request: Pick<ManualMappingRequest, 'ghl_status_id' | 'ghl_status_code'>
    ): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/status-mapping/manual/${suggestionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to update manual mapping'));
        }
    },
};
